import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Spin, Timeline, Typography } from 'antd';
// Api
import { GET_TICKET_ORDER_HISTORY } from 'api/ticketing/queries';
// Types
import {
  GetTicketOrderHistory,
  GetTicketOrderHistoryVariables,
} from 'api/ticketing/types/GetTicketOrderHistory';
import { TicketOrderKind, TicketOrderStatus } from 'api/graphql-global-types';
// Helpers
import {
  formatEventDateTime,
  getOrderStatusMeta,
  getTicketingErrorMessage,
} from 'pages/Events/helpers';
// Styles
import styles from './OrderHistory.module.scss';

const { Text } = Typography;

type OrderHistoryProps = {
  orderId: string;
  kind: TicketOrderKind;
  timezone?: string | null;
};

const OrderHistory = ({
  orderId,
  kind,
  timezone,
}: OrderHistoryProps): JSX.Element => {
  const { data, loading, error } = useQuery<
    GetTicketOrderHistory,
    GetTicketOrderHistoryVariables
  >(GET_TICKET_ORDER_HISTORY, {
    variables: { input: { orderId } },
    fetchPolicy: 'cache-and-network',
  });

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={getTicketingErrorMessage(error, 'Failed to load the history')}
      />
    );
  }

  if (loading && !data) {
    return <Spin size="small" />;
  }

  const label = (status: TicketOrderStatus) =>
    getOrderStatusMeta(status, kind).label;

  return (
    <Timeline className={styles.root}>
      {(data?.ticketOrderHistory || []).map((step) => (
        <Timeline.Item
          key={step.id}
          color={step.toStatus === TicketOrderStatus.Refunded ? 'gray' : 'blue'}
        >
          <Text strong>
            {step.fromStatus
              ? `${label(step.fromStatus)} → ${label(step.toStatus)}`
              : `Created as ${label(step.toStatus)}`}
          </Text>
          <div>
            <Text type="secondary">
              {formatEventDateTime(step.createdAt, timezone)}
              {step.actor ? ` · ${step.actor.name}` : ' · System'}
            </Text>
          </div>
          {step.reason && <div className={styles.reason}>{step.reason}</div>}
        </Timeline.Item>
      ))}
    </Timeline>
  );
};

export default OrderHistory;
